import { loadChunkWithRetry } from './load-chunk-with-retry.js';

const interactions = ['pointerdown', 'touchstart', 'keydown', 'focusin', 'click', 'submit'];
const mountedPages = new WeakMap();

export function mountBookmarksPage(page, load = () => import('../react/bookmarks/index')) {
    if (mountedPages.has(page)) return mountedPages.get(page);

    const fallback = page.querySelector('[data-bookmarks-mvc]');
    if (!fallback) return () => {};

    let disposeMvc = window.Bookmarks.mount(fallback);
    let disposeReact;
    let disposed = false;
    let dirty = false;
    let phase = 'candidate';
    let reported = false;
    const candidate = page.querySelector('[data-bookmarks-react]');
    const recovery = page.querySelector('[data-bookmarks-recovery]');
    const markDirty = () => { dirty = true; };
    const removeCapture = () => {
        interactions.forEach((event) => fallback.removeEventListener(event, markDirty, true));
    };
    const releaseReact = () => {
        const release = disposeReact;
        disposeReact = undefined;
        if (release) release();
    };
    const dispose = () => {
        if (disposed) return;
        disposed = true;
        removeCapture();
        disposeMvc();
        releaseReact();
        window.removeEventListener('pagehide', onPageHide);
        mountedPages.delete(page);
    };
    const onPageHide = (event) => {
        if (!event.persisted) dispose();
    };
    const fail = (error) => {
        if (disposed || phase === 'failed') return;
        const failedPhase = phase;
        phase = 'failed';
        removeCapture();
        if (!reported) {
            reported = true;
            window.Sentry?.captureException?.(error, {
                tags: { bookmarks_renderer: 'react', bookmarks_phase: failedPhase },
            });
        }
        if (candidate) {
            candidate.hidden = true;
            candidate.inert = true;
            candidate.style.display = 'none';
        }
        if (failedPhase === 'active' && recovery) recovery.hidden = false;
        queueMicrotask(releaseReact);
    };

    mountedPages.set(page, dispose);
    window.addEventListener('pagehide', onPageHide);
    page.dataset.bookmarksActive = 'mvc';

    if (page.dataset.bookmarksRenderer !== 'react' || !candidate) return dispose;

    interactions.forEach((event) => fallback.addEventListener(event, markDirty, true));
    loadChunkWithRetry(load).then((module) => {
        if (disposed || dirty) {
            removeCapture();
            return;
        }
        const data = JSON.parse(page.querySelector('[data-bookmarks-data]').textContent);
        disposeReact = module.mountBookmarks(candidate, data, {
            onReady: () => {
                if (disposed || dirty || phase === 'failed') {
                    removeCapture();
                    queueMicrotask(releaseReact);
                    return false;
                }
                disposeMvc();
                disposeMvc = () => {};
                window.Alpine?.destroyTree?.(fallback);
                fallback.setAttribute('x-ignore', '');
                fallback.querySelectorAll('[id^="bookmark_"]').forEach((row) => row.removeAttribute('id'));
                fallback.hidden = true;
                fallback.inert = true;
                fallback.style.display = 'none';
                candidate.hidden = false;
                candidate.inert = false;
                candidate.style.display = '';
                phase = 'active';
                page.dataset.bookmarksActive = 'react';
                removeCapture();
                const fragmentId = window.location.hash.slice(1);
                if (fragmentId.startsWith('bookmark_')) {
                    requestAnimationFrame(() => {
                        if (!disposed && phase === 'active') document.getElementById(fragmentId)?.scrollIntoView();
                    });
                }
                return true;
            },
            onError: fail,
        });
    }).catch(fail);

    return dispose;
}

function start() {
    document.querySelectorAll('[data-bookmarks-page]').forEach((page) => mountBookmarksPage(page));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
    start();
}
